import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Form } from 'antd'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { Building } from 'models/Building'
import useSWR, { useSWRConfig } from 'swr'
import CommonFunction from 'utils/commonFunction'

import useTourUploadImage, { TourUploadImagePath } from './useTourUploadImage'

export default function useSettingTourLocation({
  handleCancel
}: {
  handleCancel: () => void
}) {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const { id: buildingId } = useParams()
  const { directory } = useDirectory()
  const isCommunity = directory === 'community'
  const apiPrefix = isCommunity ? '/v1/tour' : '/v1'
  const mapRef = useRef<HTMLDivElement>(null)
  const locationUrlValue = Form.useWatch('locationUrl', form)
  const [isValidSharedLink, setIsValidSharedLink] = useState(false)
  // URL for iframe rendering
  const [embedLocationUrl, setEmbedLocationUrl] = useState<string | null>(null)
  const {
    data: buildingInfo,
    isLoading,
    mutate: mutateBuildingInfo
  } = useSWR<Building>({
    url: `${apiPrefix}/buildings/${buildingId}`
  })

  // Use global mutate from useSWRConfig to revalidate SWR cache after delete
  const { mutate } = useSWRConfig()

  /**
   * Helper to refresh building data in SWR cache
   * This ensures other components (e.g. ModalEditBuildingDetail) get updated data
   */
  const refreshBuildingData = () => {
    mutate(
      (key: any) => {
        const url = typeof key === 'string' ? key : key?.url
        return (
          url?.includes(`${apiPrefix}/buildings/by-type`) ||
          url === `${apiPrefix}/buildings/${buildingId}`
        )
      },
      undefined,
      { revalidate: true }
    )
  }

  const { trigger: updateMap, isMutating: updating } = useMutation(
    `${apiPrefix}/buildings/${buildingId}/update-extra-info`
  )

  const { trigger: deleteMap, isMutating: deleting } = useMutation(
    `${apiPrefix}/buildings/${buildingId}/update-extra-info`,
    {},
    true
  )

  const { uploadFileDescription, isUploading } = useTourUploadImage({
    buildingId: Number(buildingId),
    path: TourUploadImagePath.LOCATION
  })

  // save location url
  const handleSaveMap = async () => {
    if (!locationUrlValue?.trim()) return

    const file = new File([locationUrlValue], 'location.txt', {
      type: 'text/plain',
      lastModified: new Date().getTime()
    })
    await uploadFileDescription(file, {
      onSuccess: (key) => {
        // update location url to database
        updateMap({
          method: 'patch',
          data: {
            location: key
          },
          successMessage: t('building.saved')
        }).then(() => {
          // Optimistic update: immediately update location data in SWR cache
          // This prevents old map from flashing when modal reopens
          mutateBuildingInfo(
            (current) => {
              if (!current) return current
              return {
                ...current,
                extraInfo: {
                  ...current.extraInfo,
                  locationUrl: locationUrlValue,
                  // Clear old embed URL; backend will resolve the new one on revalidation
                  locationEmbed: ''
                }
              }
            },
            { revalidate: true }
          )

          // Close modal after successful save
          handleCancel()
        })
      }
    })
  }

  // delete location url
  const handleDeleteTourLocation = () => {
    deleteMap({
      method: 'patch',
      data: {
        location: ''
      },
      successMessage: t('building.deleted')
    }).then(() => {
      // Reset form field to clear the URL input
      form.setFieldsValue({ locationUrl: '' })

      // Reset embed URL state to clear the iframe
      setEmbedLocationUrl(null)

      // Optimistic update: immediately clear location data in SWR cache
      // This prevents stale data from re-populating the form when modal reopens
      mutateBuildingInfo(
        (current) => {
          if (!current) return current
          return {
            ...current,
            extraInfo: {
              ...current.extraInfo,
              locationUrl: '',
              locationEmbed: ''
            }
          }
        },
        { revalidate: true }
      )

      // Also revalidate building list for other components
      refreshBuildingData()

      // Close modal after successful deletion
      handleCancel()
    })
  }

  // validate location url
  useEffect(() => {
    const iframePattern: RegExp =
      /^<iframe\s+src="https:\/\/www\.google\.com\/maps\/embed\?.*?"\s+width="\d+"\s+height="\d+"\s+style="[^"]*"\s+allowfullscreen.*?><\/iframe>$/

    const shortMapUrlPattern: RegExp = /^https:\/\/maps\.app\.goo\.gl\/\S+$/

    const fullMapUrlPattern: RegExp =
      /^https:\/\/(www\.)?google\.com\/maps\/.*$/

    if (
      iframePattern.test(locationUrlValue) ||
      shortMapUrlPattern.test(locationUrlValue) ||
      fullMapUrlPattern.test(locationUrlValue)
    ) {
      setIsValidSharedLink(true)
    } else {
      setIsValidSharedLink(false)
    }

    if (mapRef.current && locationUrlValue !== undefined) {
      if (
        locationUrlValue?.trim() !== '' &&
        iframePattern.test(locationUrlValue) &&
        locationUrlValue
      ) {
        mapRef.current.innerHTML = locationUrlValue
      } else {
        mapRef.current.innerHTML = ''
      }
    }
  }, [locationUrlValue])

  // Handle location URL: Fetch content if it is a Digitori360 S3 link, otherwise use directly
  useEffect(() => {
    const handleFetchLocationUrl = async () => {
      if (buildingInfo && buildingInfo.extraInfo.locationUrl) {
        const url = buildingInfo.extraInfo.locationUrl

        // Check if it is a Digitori360 S3 link
        const s3Domain = process.env.REACT_APP_S3_DOMAIN
        const isS3InputLink = s3Domain && url.startsWith(s3Domain)

        if (isS3InputLink) {
          // Case S3: Fetch content
          const fetchedContent =
            await CommonFunction.FetchTourLocationContentFromS3Link(url)

          form.setFieldsValue({
            locationUrl: fetchedContent
          })
        } else {
          // Case Non-S3: Use direct URL
          form.setFieldsValue({
            locationUrl: url
          })
        }
      }

      // Set expanded URL for iframe rendering
      if (buildingInfo?.extraInfo?.locationEmbed) {
        setEmbedLocationUrl(buildingInfo.extraInfo.locationEmbed)
      }
    }

    handleFetchLocationUrl()
  }, [buildingInfo, form])

  return {
    handleSaveMap,
    handleDeleteTourLocation,
    form,
    mapRef,
    locationUrlValue,
    embedLocationUrl, // URL for iframe rendering (expanded by backend)
    updating,
    isLoading,
    deleting,
    isValidSharedLink,
    isUploading,
    t
  }
}
